import React, { useState, useContext, useEffect } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Link,
  TablePagination,
  Typography,
  withStyles
} from '@material-ui/core';
import Rating from '@material-ui/lab/Rating';
import { I18nContext } from 'translations';
import { format } from 'date-fns';
import { getRateReasons } from 'context/rates/actions';
import EmptyData from '../EmptyData';

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  return parts.length >= 2
    ? `${parts[0][0]}${parts[1][0]}`.toUpperCase()
    : parts[0][0].toUpperCase();
};

const styles = (theme) => ({
  wrapper: {
    borderRadius: 16,
    padding: '4px 0',
  },
  card: {
    borderRadius: 16,
    marginBottom: 12,
    padding: '16px 20px',
  },
  avatar: {
    backgroundColor: theme.palette.primary.main,
    width: 44,
    height: 44,
    fontSize: '0.85rem',
    fontWeight: 700,
    marginRight: 14,
  },
  sendBtn: {
    borderRadius: 24,
    textTransform: 'none',
    padding: '4px 15px',
    marginLeft: 16,
    minWidth: 85,
    fontSize: '0.92rem',
    '&.Mui-disabled': {
      backgroundColor: theme.palette.primary.light,
      color: '#fff',
      opacity: 0.6,
    },
  },
  reasonsSection: {
    backgroundColor: '#f8f8fb',
    borderRadius: 12,
    marginTop: 12,
    padding: '12px 14px',
  },
  chip: {
    height: '32px',
    borderColor: '#eee',
    '& .MuiChip-label': {
      fontSize: '0.72rem',
      fontWeight: 600,
      padding: '5px 12px'
    },
    '&:not(.MuiChip-colorPrimary)': {
      backgroundColor: '#fff'
    },
  },
});

const RateBookingsTable = ({
  classes,
  data,
  sendRate,
  recordsPerPage,
  getUnqulifiedBookings
}) => {
  const [ratings, setRatings] = useState({});
  const [allReasons, setAllReasons] = useState({});
  const [allQuestions, setAllQuestions] = useState({});
  const [reasons, setReasons] = useState({});
  const [question, setQuestion] = useState({});
  const [selectedReasons, setSelectedReasons] = useState({});
  const [loadingReasons, setLoadingReasons] = useState({});
  const [currentPage, setCurrentPage] = useState(data.page);
  const { translate } = useContext(I18nContext);

  useEffect(() => {
    setCurrentPage(data.page);
  }, [data]);

  const handleChangePage = (event, newPage) => {
    setCurrentPage(newPage);
    getUnqulifiedBookings(newPage);
  };

  const onChangeRating = async (bookingId, newValue) => {
    setRatings(prev => ({ ...prev, [bookingId]: newValue }));
    setSelectedReasons(prev => ({ ...prev, [bookingId]: [] }));

    if (!newValue) {
      setReasons(prev => ({ ...prev, [bookingId]: [] }));
      setQuestion(prev => ({ ...prev, [bookingId]: null }));
      return;
    }

    if (allReasons[bookingId]) {
      setReasons(prev => ({ ...prev, [bookingId]: allReasons[bookingId][newValue - 1] || [] }));
      setQuestion(prev => ({ ...prev, [bookingId]: allQuestions[bookingId]?.[newValue - 1] || null }));
      return;
    }

    setLoadingReasons(prev => ({ ...prev, [bookingId]: true }));
    const response = await getRateReasons(bookingId);
    const stars = response?.stars || [];
    const questions = response?.questions || [];
    setAllReasons(prev => ({ ...prev, [bookingId]: stars }));
    setAllQuestions(prev => ({ ...prev, [bookingId]: questions }));
    setReasons(prev => ({ ...prev, [bookingId]: stars[newValue - 1] || [] }));
    setQuestion(prev => ({ ...prev, [bookingId]: questions[newValue - 1] || null }));
    setLoadingReasons(prev => ({ ...prev, [bookingId]: false }));
  };

  const onSelectReason = (bookingId, code) => {
    setSelectedReasons(prev => {
      const current = prev[bookingId] || [];
      const already = current.includes(code);
      return {
        ...prev,
        [bookingId]: already ? current.filter(c => c !== code) : [...current, code]
      };
    });
  };

  if (!data || (data && data.data.length <= 0)) {
    return <EmptyData message={translate('thereIsNoData')} />;
  }

  return (
    <>
      <Box className={classes.wrapper}>
        {(data.data || []).map(booking => {
          const bookingReasons = reasons[booking._id] || [];
          const bookingQuestion = question[booking._id];
          const bookingRating = ratings[booking._id] || 0;
          const bookingSelected = selectedReasons[booking._id] || [];
          const isLoading = loadingReasons[booking._id];
          const hasReasons = bookingQuestion || bookingReasons.length > 0;

          return (
            <Card key={booking._id} className={classes.card} elevation={1}>
              <Box display="flex" alignItems="center">
                <Avatar
                  className={classes.avatar}
                  alt={booking.driver?.name || ''}
                  src={booking.driver?.photo_url || ''}
                >
                  {!booking.driver?.photo_url && getInitials(booking.driver?.name)}
                </Avatar>

                <Box flex={1} minWidth={0}>
                  <Typography style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1a1a1a', lineHeight: 1.3 }}>
                    {booking.driver ? booking.driver.name : '-'}
                  </Typography>
                  <Typography variant="caption" style={{ display: 'block', color: '#292525', marginTop: 1 }}>
                    {format(new Date(booking.created_at), 'dd/MM/yyyy, hh:mm a')}
                  </Typography>
                  <Link
                    href={`/bookings/${booking._id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                  >
                    <Typography variant="caption" style={{ color: 'rgb(109 104 104)', fontFamily: 'monospace' }}>
                      #{booking._id}
                    </Typography>
                  </Link>
                </Box>

                <Box display="flex" alignItems="center" flexShrink={0}>
                  {isLoading && <CircularProgress size={18} style={{ marginRight: 10 }} />}
                  <Rating
                    size="medium"
                    name={booking._id}
                    value={bookingRating}
                    onChange={(_, newValue) => onChangeRating(booking._id, newValue)}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    className={classes.sendBtn}
                    disabled={!bookingRating}
                    onClick={() => sendRate(booking._id, ratings, bookingSelected)}
                  >
                    {translate('send')}
                  </Button>
                </Box>
              </Box>

              {hasReasons && (
                <Box className={classes.reasonsSection}>
                  {bookingQuestion && (
                    <Typography variant="caption" style={{ color: '#000', marginBottom: 15, display: 'block', fontSize: '0.79rem', fontWeight: 'bold' }}>
                      {bookingQuestion}
                    </Typography>
                  )}
                  <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
                    {bookingReasons.map(reason => {
                      const isSelected = bookingSelected.includes(reason.code);
                      return (
                        <Chip
                          key={reason.code}
                          label={reason.name}
                          clickable
                          size="small"
                          color={isSelected ? 'primary' : 'default'}
                          variant={isSelected ? 'default' : 'outlined'}
                          className={classes.chip}
                          onClick={() => onSelectReason(booking._id, reason.code)}
                        />
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Card>
          );
        })}
      </Box>
      <TablePagination
        component="div"
        count={data.total ? data.total : data.pages * recordsPerPage}
        page={currentPage - 1}
        rowsPerPageOptions={[]}
        rowsPerPage={recordsPerPage}
        onChangePage={(event, newPage) => handleChangePage(event, newPage + 1)}
      />
    </>
  );
};

export default withStyles(styles)(RateBookingsTable);
